import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Wifi, WifiOff, LogOut } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Ribbons from "@/components/Ribbons";
import { useApp } from "@/store/AppContext";
import { useLive } from "@/store/LiveContext";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { getUser, clearAuth } from "@/lib/auth";

function UserMenu() {
  const navigate = useNavigate();
  const user = getUser();
  const logout = () => {
    clearAuth();
    navigate("/login", { replace: true });
  };
  return (
    <div className="flex items-center gap-2" data-testid="user-menu">
      {user && <span className="text-[11px] font-semibold text-slate-600">{user}</span>}
      <button
        onClick={logout}
        data-testid="logout-btn"
        title="Sign out"
        className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900 border border-slate-200 rounded px-2 py-1 transition-colors"
      >
        <LogOut size={13} /> Sign out
      </button>
    </div>
  );
}

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="font-mono text-xs text-slate-500" data-testid="header-clock">
      {now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}{" "}
      {now.toLocaleTimeString("en-IN", { hour12: false })}
    </span>
  );
}

function ModeToggle() {
  const { mode, setMode } = useApp();
  return (
    <div className="flex items-center bg-slate-100 rounded-md p-0.5" data-testid="mode-toggle">
      {["executive", "engineering"].map((m) => (
        <button
          key={m}
          data-testid={`mode-${m}`}
          onClick={() => setMode(m)}
          className={`px-3 py-1 text-[11px] font-semibold rounded uppercase tracking-wide transition-colors ${
            mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
          }`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

function ModeSwitch() {
  const { data, refresh } = usePoll(endpoints.mode, 10000);
  const mode = data?.mode || "Simulation";
  const COLORS = { Simulation: "#2563eb", Shadow: "#f59e0b", Production: "#10b981" };
  const change = async (e) => {
    await endpoints.setMode(e.target.value);
    refresh();
  };
  return (
    <div className="flex items-center gap-1.5" data-testid="mode-switch">
      <span className="w-2 h-2 rounded-full live-dot" style={{ background: COLORS[mode] }} />
      <select value={mode} onChange={change} data-testid="mode-select"
        className="text-[11px] font-semibold bg-transparent border border-slate-200 rounded px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-slate-300 cursor-pointer"
        style={{ color: COLORS[mode] }}>
        <option value="Simulation">Simulation · LIVE</option>
        <option value="Shadow">Shadow · LIVE</option>
        <option value="Production">Production · LIVE</option>
      </select>
    </div>
  );
}

function WsIndicator() {
  const { connected } = useLive();
  return (
    <span className={`flex items-center gap-1 text-[10px] font-semibold ${connected ? "text-emerald-600" : "text-slate-400"}`}
      data-testid="ws-indicator" title={connected ? "Realtime WebSocket connected" : "Polling fallback"}>
      {connected ? <Wifi size={13} /> : <WifiOff size={13} />}
      {connected ? "REALTIME" : "POLLING"}
    </span>
  );
}

export default function AppLayout() {
  return (
    <div className="flex min-h-screen bg-slate-100">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-[52px] bg-white border-b border-slate-200 flex items-center justify-between px-5 sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-900">Dairy Enterprise — Main Plant</span>
            <ModeSwitch />
          </div>
          <div className="flex items-center gap-4">
            <WsIndicator />
            <Clock />
            <ModeToggle />
            <UserMenu />
          </div>
        </header>
        <Ribbons />
        <main className="flex-1 p-4 lg:p-6 fade-up" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
